import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Select } from '@liminal/web-ui/forms/Select';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { useDeleteSuspension, useSuspension, useUpdateSuspension } from '@/hooks/useSuspension';
import { getErrorMessage } from '@/errors/messages';

// Suspension actions on the user detail header (LIM-2453). A suspension is a
// future expiry; a ban is permanent. Enforcement is immediate — auth updates
// the cache the gateways check on every request as part of the same call.
//
// This once hand-built its dialog because ConfirmationDialog had no children
// slot; the slot exists now (a confirm that needs a duration and a reason is
// still a confirm), so the shared component carries it.

type DialogMode = 'suspend' | 'ban' | 'lift';

const DURATION_OPTIONS = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
] as const;

// @liminal/web-ui/forms/Select speaks string values; hours are parsed back on change.
const DURATION_SELECT_OPTIONS = DURATION_OPTIONS.map((option) => ({
  value: String(option.hours),
  label: option.label,
}));

const DIALOG_COPY: Record<DialogMode, { title: string; confirmLabel: string; blurb: string }> = {
  suspend: {
    title: 'Suspend user',
    confirmLabel: 'Suspend',
    blurb: 'They are signed out of every session and blocked from the API until the suspension expires.',
  },
  ban: {
    title: 'Ban user permanently',
    confirmLabel: 'Ban permanently',
    blurb: 'They are blocked from the API indefinitely. This can be lifted again at any time.',
  },
  lift: {
    title: 'Lift suspension',
    confirmLabel: 'Lift suspension',
    blurb: 'They regain access on their next request.',
  },
};

export function SuspensionActions({ userId }: { userId: string }) {
  const { data: suspension, isLoading } = useSuspension(userId);
  const updateSuspension = useUpdateSuspension(userId);
  const deleteSuspension = useDeleteSuspension(userId);

  const [mode, setMode] = useState<DialogMode | null>(null);
  const [reason, setReason] = useState('');
  const [durationHours, setDurationHours] = useState<number>(DURATION_OPTIONS[0].hours);
  const [error, setError] = useState<string | null>(null);

  const openDialog = (next: DialogMode) => {
    setMode(next);
    setReason('');
    setDurationHours(DURATION_OPTIONS[0].hours);
    setError(null);
  };

  const closeDialog = () => {
    setMode(null);
    setError(null);
  };

  const submit = async () => {
    if (!mode) return;
    setError(null);
    try {
      if (mode === 'lift') {
        await deleteSuspension.mutateAsync({ reason });
      } else if (mode === 'ban') {
        await updateSuspension.mutateAsync({ permanent: true, reason });
      } else {
        // A moment in time: sent as UTC ISO, rendered in the viewer's zone.
        const until = new Date(Date.now() + durationHours * 60 * 60 * 1000);
        await updateSuspension.mutateAsync({ suspendedUntil: until.toISOString(), reason });
      }
      closeDialog();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  if (isLoading) return null;

  const isSuspended = suspension?.suspended ?? false;
  const pending = updateSuspension.isPending || deleteSuspension.isPending;

  return (
    <Stack spacing={1} alignItems="flex-end">
      {isSuspended && (
        <>
          <StatusPill
            label={
              suspension?.permanent
                ? 'Banned'
                : `Suspended until ${formatUntil(suspension?.suspendedUntil)}`
            }
            tone="error"
          />
          {/* Absent when the suspension was recorded without them. */}
          {suspension?.reason && (
            <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'right' }}>
              {suspension.reason}
            </Typography>
          )}
          {suspension?.actorEmail && (
            <Typography variant="caption" color="text.disabled">
              by {suspension.actorEmail}
            </Typography>
          )}
        </>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        {isSuspended ? (
          <Button variant="secondary" size="sm" onClick={() => openDialog('lift')}>
            Lift suspension
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={() => openDialog('suspend')}>
              Suspend
            </Button>
            <Button variant="danger" size="sm" onClick={() => openDialog('ban')}>
              Ban
            </Button>
          </>
        )}
      </Box>

      {mode && (
        <ConfirmationDialog
          open
          title={DIALOG_COPY[mode].title}
          message={DIALOG_COPY[mode].blurb}
          confirmLabel={DIALOG_COPY[mode].confirmLabel}
          destructive={mode !== 'lift'}
          loading={pending}
          onConfirm={() => void submit()}
          onCancel={closeDialog}
          data-testid="suspension-dialog"
        >
          <Stack spacing={1} sx={{ mt: 1 }}>
            {mode === 'suspend' && (
              <Select
                label="Duration"
                size="sm"
                value={String(durationHours)}
                onChange={(value) => setDurationHours(Number(value))}
                options={DURATION_SELECT_OPTIONS}
              />
            )}

            <Textarea
              label="Reason"
              value={reason}
              onChange={setReason}
              placeholder="Shown on the user's record and in the audit log"
              rows={2}
            />

            <FormErrorBanner error={error ?? undefined} />
          </Stack>
        </ConfirmationDialog>
      )}
    </Stack>
  );
}

// Renders the expiry in the viewer's own timezone from the UTC value the
// server sends.
function formatUntil(suspendedUntil: string | undefined): string {
  if (!suspendedUntil) return 'further notice';
  return new Date(suspendedUntil).toLocaleString();
}
